/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateStorage } from 'zustand/middleware';

type DebouncedStorageOptions = {
  /**
   * Debounce delay in milliseconds
   * @default 1000
   */
  delay?: number;
  /**
   * Maximum wait time before forcing a write (in ms)
   * Prevents indefinite delays from continuous updates
   * @default 5000
   */
  maxWait?: number;
};

type PendingWrite = {
  value: any;
  timeout: NodeJS.Timeout;
  timestamp: number;
};

/**
 * Wraps a StateStorage with debouncing to reduce expensive operations:
 * - Encryption/decryption via web worker
 * - postMessage calls to worker
 * - IndexedDB/LocalStorage write operations
 *
 * State updates remain instant in memory while persistence is debounced.
 *
 * @example
 * ```ts
 * const storage = createDebouncedStorage(
 *   indexedDBStorage({ ... }),
 *   { delay: 1000, maxWait: 5000 }
 * );
 * ```
 */
export function createDebouncedStorage(
  storage: StateStorage,
  options: DebouncedStorageOptions = {}
): StateStorage & { flush: () => Promise<void> } {
  const { delay = 1000, maxWait = 5000 } = options;

  const pendingWrites = new Map<string, PendingWrite>();

  /**
   * Execute the actual storage write
   */
  const executeWrite = async (name: string, value: any): Promise<void> => {
    pendingWrites.delete(name);
    try {
      await storage.setItem(name, value);
    } catch (error) {
      console.error(`Debounced storage write failed for key "${name}":`, error);
      throw error;
    }
  };

  /**
   * Schedule or reschedule a write operation
   */
  const scheduleWrite = (name: string, value: any): void => {
    const now = Date.now();
    const existing = pendingWrites.get(name);

    // Clear existing timeout
    if (existing) {
      clearTimeout(existing.timeout);

      // Force write if maxWait time has elapsed
      if (now - existing.timestamp >= maxWait) {
        void executeWrite(name, value).catch(() => {
          // Error already logged in executeWrite; swallow to avoid unhandled rejection
        });
        return;
      }
    }

    // Schedule new write
    const timeout = setTimeout(() => {
      void executeWrite(name, value).catch(() => {
        // Error already logged in executeWrite; swallow to avoid unhandled rejection
      });
    }, delay);

    pendingWrites.set(name, {
      value,
      timeout,
      timestamp: existing?.timestamp ?? now, // Keep original timestamp
    });
  };

  return {
    getItem: (name: string) => storage.getItem(name),
    removeItem: (name: string) => storage.removeItem(name),

    /**
     * Debounced setItem - batches rapid writes
     */
    setItem: (name: string, value: any): void => {
      scheduleWrite(name, value);
    },

    /**
     * Immediately flush all pending writes
     * Useful for:
     * - Before page unload
     * - After critical operations
     * - Before logout
     */
    flush: async (): Promise<void> => {
      const writes = Array.from(pendingWrites.entries()).map(
        ([name, { value, timeout }]) => {
          clearTimeout(timeout);
          return executeWrite(name, value);
        }
      );

      await Promise.all(writes);
    },
  };
}

/**
 * Auto-flush on page unload/visibility change
 * Call this once in your app initialization
 *
 * @example
 * ```ts
 * const storage = createDebouncedStorage(indexedDBStorage({ ... }));
 * setupAutoFlush(storage);
 * ```
 */
export function setupAutoFlush(
  storage: StateStorage & { flush: () => Promise<void> }
): () => void {
  const handleFlush = () => {
    // Use synchronous beacon API or immediate flush
    storage.flush().catch((err) => {
      console.error('Auto-flush failed:', err);
    });
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      handleFlush();
    }
  };

  // Flush before page unload
  window.addEventListener('beforeunload', handleFlush);
  // Flush when page becomes hidden (mobile, tab switch)
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Return cleanup function
  return () => {
    window.removeEventListener('beforeunload', handleFlush);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}
